import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DarkLayout from './DarkLayout';
import { HeaderPrimaryAddButton } from './HeaderPrimaryAddButton';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { apiService } from '../services/api';
import {
  DocumentArchiveListItem,
  DocumentArchiveCustodianLog,
  CustodianOption,
  DocumentArchiveStats,
  ShareableUser,
  VaultStatus,
  DriveFolderEntry,
  Project,
} from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  InputLabel,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { listPageDialogPaperProps } from '../theme/darkDialog';

const listPageDialogTitleSx = { fontSize: '1.5rem', fontWeight: 600, pb: 1 } as const;
const listPageDialogContentSx = { px: 3, pt: '20px' } as const;
const listPageDialogActionsSx = { px: 3, pb: 3, gap: 1 } as const;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function logSummary(log: DocumentArchiveCustodianLog): string {
  const prev = log.previousCustodianDisplayName ?? 'Unassigned';
  const next = log.newCustodianDisplayName ?? 'Unassigned';
  if (!log.previousCustodianUserId && log.newCustodianUserId) return `Assigned to ${next}`;
  if (log.previousCustodianUserId && !log.newCustodianUserId) return `Unassigned (was ${prev})`;
  return `Reassigned from ${prev} to ${next}`;
}

function archiveSubPathFromBrowseFullPath(defaultRoot: string, fullPath: string): string {
  const r = defaultRoot.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  const f = fullPath.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  if (f.toLowerCase() === r.toLowerCase()) return '';
  const prefix = `${r}/`;
  if (f.toLowerCase().startsWith(prefix.toLowerCase())) return f.slice(prefix.length);
  return f;
}

function fullBrowsePathFromArchiveSubPath(defaultRoot: string, subPath: string): string {
  const r = defaultRoot.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  const s = (subPath || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  if (!s) return r;
  return `${r}/${s}`.replace(/\/+/g, '/');
}

function apiErrorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object' && e.response.data !== null) {
    const m = (e.response.data as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  return fallback;
}

const DocumentArchives: React.FC = () => {
  useDocumentTitle('Document Archives');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();

  const canCreate = hasPermission(user, permissions.documentArchives.create);
  const canEdit = hasPermission(user, permissions.documentArchives.edit);
  const canDelete = hasPermission(user, permissions.documentArchives.delete);
  const canConfigureVault = hasPermission(user, permissions.documentArchives.configureVault);

  const [items, setItems] = useState<DocumentArchiveListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stats, setStats] = useState<DocumentArchiveStats | null>(null);
  const [vaultStatus, setVaultStatus] = useState<VaultStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProjectId, setUploadProjectId] = useState<string>('');
  const [uploadCustodian, setUploadCustodian] = useState<number | ''>('');
  const [projectOptions, setProjectOptions] = useState<Project[]>([]);
  const [custodianOptions, setCustodianOptions] = useState<CustodianOption[]>([]);
  const [shareableUsers, setShareableUsers] = useState<ShareableUser[]>([]);
  const [uploadShareWith, setUploadShareWith] = useState<ShareableUser[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadDocumentName, setUploadDocumentName] = useState('');
  const [targetFolderSubPath, setTargetFolderSubPath] = useState('');

  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderBrowsePath, setFolderBrowsePath] = useState('');
  const [folderEntries, setFolderEntries] = useState<DriveFolderEntry[]>([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [cloudOpen, setCloudOpen] = useState(false);
  const [cloudDoc, setCloudDoc] = useState<DocumentArchiveListItem | null>(null);
  const [cloudFile, setCloudFile] = useState<File | null>(null);
  const [clouding, setClouding] = useState(false);
  const [cloudTargetFolderSubPath, setCloudTargetFolderSubPath] = useState('');
  const [folderPickerMode, setFolderPickerMode] = useState<'add' | 'cloud'>('add');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState<DocumentArchiveCustodianLog[]>([]);
  const [historyTitle, setHistoryTitle] = useState('');

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDocId, setAssignDocId] = useState<number | null>(null);
  const [assignValue, setAssignValue] = useState<number | '' | 'unassigned'>('unassigned');
  const [assignDocumentName, setAssignDocumentName] = useState('');
  const [assignProjectId, setAssignProjectId] = useState<string>('');
  const [assignShareWith, setAssignShareWith] = useState<ShareableUser[]>([]);
  const [assigning, setAssigning] = useState(false);

  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [paged, st, vs] = await Promise.all([
        apiService.getDocumentArchives({
          searchTerm: debouncedSearch || undefined,
          pageNumber: page,
          pageSize,
          sortBy: 'uploadedAt',
          sortDescending: true,
        }),
        apiService.getDocumentArchiveStats(),
        canConfigureVault ? apiService.getVaultStatus() : Promise.resolve(null),
      ]);
      setItems(paged.items);
      setTotalCount(paged.totalCount);
      setStats(st);
      setVaultStatus(vs);
    } catch (e) {
      console.error(e);
      setError('Failed to load document archives');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, canConfigureVault]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const vault = searchParams.get('vault');
    if (vault === 'connected') {
      setSnackbar({ message: 'Shared archive vault connected successfully.', severity: 'success' });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('vault');
          return next;
        },
        { replace: true }
      );
    } else if (vault === 'error') {
      const msg = searchParams.get('message') || 'Vault connection failed';
      setSnackbar({ message: decodeURIComponent(msg), severity: 'error' });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('vault');
          next.delete('message');
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const loadCustodianOptions = async () => {
    try {
      const opts = await apiService.getCustodianOptions();
      setCustodianOptions(opts);
    } catch {
      setCustodianOptions([]);
    }
  };

  const openUpload = async () => {
    setUploadFile(null);
    setUploadDocumentName('');
    setTargetFolderSubPath('');
    setUploadProjectId('');
    setUploadCustodian('');
    setUploadShareWith([]);
    await Promise.all([
      loadCustodianOptions(),
      (async () => {
        try {
          setProjectOptions(await apiService.getProjects());
        } catch {
          setProjectOptions([]);
        }
      })(),
      (async () => {
        if (canCreate) {
          try {
            const su = await apiService.getDocumentArchiveShareableUsers();
            setShareableUsers(su);
          } catch {
            setShareableUsers([]);
          }
        } else {
          setShareableUsers([]);
        }
      })(),
    ]);
    setUploadOpen(true);
  };

  const openFolderDialog = (mode: 'add' | 'cloud' = 'add') => {
    if (!stats?.vaultConnected) {
      setSnackbar({ message: 'Connect the shared vault to browse OneDrive folders.', severity: 'info' });
      return;
    }
    setFolderPickerMode(mode);
    const root = stats?.defaultArchiveRootFolder ?? 'DocumentArchives';
    const sub = mode === 'cloud' ? cloudTargetFolderSubPath : targetFolderSubPath;
    const startPath = fullBrowsePathFromArchiveSubPath(root, sub);
    setFolderBrowsePath(startPath);
    setNewFolderName('');
    setFolderDialogOpen(true);
    void refreshFolderList(startPath);
  };

  const refreshFolderList = async (path: string) => {
    setFolderLoading(true);
    try {
      const list = await apiService.listDocumentArchiveDriveFolder(path);
      setFolderEntries(list);
    } catch (e) {
      console.error(e);
      setSnackbar({ message: apiErrorMessage(e, 'Could not list folder.'), severity: 'error' });
      setFolderEntries([]);
    } finally {
      setFolderLoading(false);
    }
  };

  const enterSubfolder = (name: string) => {
    const next = folderBrowsePath.includes('/')
      ? `${folderBrowsePath.replace(/\/+$/, '')}/${name}`
      : `${folderBrowsePath}/${name}`.replace('//', '/');
    setFolderBrowsePath(next);
    void refreshFolderList(next);
  };

  const goToParentFolder = () => {
    const root = stats?.defaultArchiveRootFolder ?? 'DocumentArchives';
    const norm = (s: string) => s.replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\/+|\/+$/g, '');
    const r = norm(root);
    const f = norm(folderBrowsePath);
    if (f.toLowerCase() === r.toLowerCase()) return;
    if (!f.toLowerCase().startsWith(r.toLowerCase() + '/') && f.toLowerCase() !== r.toLowerCase()) return;
    const rel = f.length > r.length ? f.slice(r.length).replace(/^\//, '') : '';
    const parts = rel ? rel.split('/').filter(Boolean) : [];
    if (parts.length === 0) return;
    parts.pop();
    const up = parts.length > 0 ? `${r}/${parts.join('/')}` : r;
    setFolderBrowsePath(up);
    void refreshFolderList(up);
  };

  const applyPickedFolder = () => {
    const root = stats?.defaultArchiveRootFolder ?? 'DocumentArchives';
    const sub = archiveSubPathFromBrowseFullPath(root, folderBrowsePath);
    if (folderPickerMode === 'cloud') {
      setCloudTargetFolderSubPath(sub);
    } else {
      setTargetFolderSubPath(sub);
    }
    setFolderDialogOpen(false);
  };

  const createSubfolder = async () => {
    if (!newFolderName.trim()) return;
    if (!stats?.vaultConnected) return;
    setFolderLoading(true);
    try {
      await apiService.createDocumentArchiveDriveFolder(folderBrowsePath, newFolderName.trim());
      setNewFolderName('');
      await refreshFolderList(folderBrowsePath);
      setSnackbar({ message: 'Folder created.', severity: 'success' });
    } catch (e) {
      setSnackbar({ message: apiErrorMessage(e, 'Could not create folder.'), severity: 'error' });
    } finally {
      setFolderLoading(false);
    }
  };

  const submitUpload = async () => {
    const withFile = !!(uploadFile && uploadFile.size > 0);
    if (!withFile) {
      if (!uploadDocumentName.trim()) {
        setSnackbar({ message: 'Enter a document name for a metadata-only entry, or add a file.', severity: 'error' });
        return;
      }
    } else {
      if (!stats?.vaultConnected) {
        setSnackbar({ message: 'Connect the shared vault to upload a file to OneDrive.', severity: 'error' });
        return;
      }
    }
    try {
      setUploading(true);
      await apiService.uploadDocumentArchive(withFile ? uploadFile! : null, {
        documentName: uploadDocumentName.trim() || undefined,
        projectId: uploadProjectId || null,
        targetFolderSubPath: targetFolderSubPath || undefined,
        custodianUserId: uploadCustodian === '' ? null : uploadCustodian,
        shareWithUserIds: uploadShareWith.length > 0 ? uploadShareWith.map((u) => u.userId) : undefined,
      });
      setUploadOpen(false);
      setSnackbar({ message: withFile ? 'Document uploaded.' : 'Entry saved (local metadata).', severity: 'success' });
      await load();
    } catch (e) {
      console.error(e);
      const is409 = axios.isAxiosError(e) && e.response?.status === 409;
      setSnackbar({
        message: is409
          ? apiErrorMessage(e, 'A document with this name already exists in that folder.')
          : apiErrorMessage(e, 'Create failed. Check vault connection and try again.'),
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const openHistory = async (row: DocumentArchiveListItem) => {
    try {
      const logs = await apiService.getDocumentArchiveCustodianLogs(row.documentArchiveId);
      setHistoryRows(logs);
      setHistoryTitle(row.originalFileName);
      setHistoryOpen(true);
    } catch {
      setSnackbar({ message: 'Failed to load history.', severity: 'error' });
    }
  };

  const openAssign = async (row: DocumentArchiveListItem) => {
    try {
      const [detail, custodianOpts, projects, shareUsers] = await Promise.all([
        apiService.getDocumentArchive(row.documentArchiveId),
        apiService.getCustodianOptions(),
        apiService.getProjects(),
        apiService.getDocumentArchiveShareableUsers(),
      ]);
      setAssignDocId(row.documentArchiveId);
      setAssignDocumentName(detail.originalFileName);
      setAssignValue(detail.custodianUserId ?? 'unassigned');
      setAssignProjectId(detail.projectId ?? '');
      setAssignShareWith(
        (detail.sharedWith ?? [])
          .map((sw) => shareUsers.find((u) => u.userId === sw.userId))
          .filter((u): u is ShareableUser => !!u)
      );
      setCustodianOptions(custodianOpts);
      setProjectOptions(projects);
      setShareableUsers(shareUsers);
      setAssignOpen(true);
    } catch {
      setSnackbar({ message: 'Could not open metadata editor.', severity: 'error' });
    }
  };

  const submitAssign = async () => {
    if (assignDocId == null) return;
    if (!assignDocumentName.trim()) {
      setSnackbar({ message: 'Document name is required.', severity: 'error' });
      return;
    }
    try {
      setAssigning(true);
      const v: number | null = assignValue === 'unassigned' ? null : (assignValue as number);
      await apiService.patchDocumentArchiveMetadata(assignDocId, {
        documentName: assignDocumentName.trim(),
        projectId: assignProjectId || null,
        custodianUserId: v,
        shareWithUserIds: assignShareWith.map((u) => u.userId),
      });
      setAssignOpen(false);
      setSnackbar({ message: 'Document metadata updated.', severity: 'success' });
      await load();
    } catch (e) {
      setSnackbar({ message: apiErrorMessage(e, 'Could not update document metadata.'), severity: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  const handleView = async (row: DocumentArchiveListItem) => {
    if (row.hasCloudCopy === false) {
      setSnackbar({
        message: 'This entry is metadata only. Upload a file to the cloud first (Upload to cloud).',
        severity: 'info',
      });
      return;
    }
    try {
      const { shareUrl, webUrl } = await apiService.getDocumentArchiveDownloadUrl(row.documentArchiveId);
      const url = shareUrl || webUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else setSnackbar({ message: 'No sharing link available.', severity: 'error' });
    } catch {
      setSnackbar({ message: 'Could not open document.', severity: 'error' });
    }
  };

  const openCloudUpload = async (row: DocumentArchiveListItem) => {
    setCloudDoc(row);
    setCloudFile(null);
    setCloudTargetFolderSubPath('');
    const root = stats?.defaultArchiveRootFolder ?? 'DocumentArchives';
    try {
      const detail = await apiService.getDocumentArchive(row.documentArchiveId);
      setCloudTargetFolderSubPath(archiveSubPathFromBrowseFullPath(root, detail.oneDriveTargetFolderPath));
    } catch {
      setCloudTargetFolderSubPath('');
    }
    setCloudOpen(true);
  };

  const submitCloudUpload = async () => {
    if (cloudDoc == null || !cloudFile) {
      setSnackbar({ message: 'Choose a file.', severity: 'error' });
      return;
    }
    if (!stats?.vaultConnected) {
      setSnackbar({ message: 'Connect the shared vault first.', severity: 'error' });
      return;
    }
    try {
      setClouding(true);
      await apiService.uploadDocumentToCloud(cloudDoc.documentArchiveId, cloudFile, {
        targetFolderSubPath: cloudTargetFolderSubPath,
      });
      setCloudOpen(false);
      setSnackbar({ message: 'File uploaded to the cloud archive.', severity: 'success' });
      await load();
    } catch (e) {
      setSnackbar({
        message: apiErrorMessage(e, 'Upload to cloud failed.'),
        severity: 'error',
      });
    } finally {
      setClouding(false);
    }
  };

  const handleDelete = async (row: DocumentArchiveListItem) => {
    const confirmed = await confirmDelete({
      title: 'Delete document archive?',
      message:
        row.hasCloudCopy === false
          ? `Remove "${row.originalFileName}" from the catalog?`
          : `Remove "${row.originalFileName}" from the catalog and the file from OneDrive?`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    try {
      await apiService.deleteDocumentArchive(row.documentArchiveId);
      setSnackbar({ message: 'Document removed.', severity: 'success' });
      await load();
    } catch {
      setSnackbar({ message: 'Delete failed.', severity: 'error' });
    }
  };

  const connectVault = async () => {
    try {
      const { authorizationUrl } = await apiService.getVaultAuthorizeUrl();
      window.location.href = authorizationUrl;
    } catch {
      setSnackbar({ message: 'Could not start Microsoft sign-in.', severity: 'error' });
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <DarkLayout
      title="Document Archives"
      subtitle="Technical vault & custody"
      activeTab="documentarchives"
      headerRight={
        canCreate ? (
          <HeaderPrimaryAddButton label="Add" onClick={() => void openUpload()} mobileAriaLabel="Add to archive" />
        ) : undefined
      }
    >
      {canConfigureVault && stats && !stats.vaultConnected && (
        <div className="mx-4 mt-4 mb-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 flex flex-wrap items-center justify-between gap-2">
          {vaultStatus?.graphAuthMode === 'AppOnly' ? (
            <span>
              Vault is configured for app-only authentication. Complete server configuration (Entra application permissions,
              DocumentArchive:AppOnlyTenantId and VaultDriveUser) — Connect Microsoft is not used.
            </span>
          ) : (
            <>
              <span>
                Connect one dedicated Microsoft account whose OneDrive will hold the shared archive for all app users. Sign in as
                that account (e.g. a service mailbox), not as an individual user&apos;s personal login.
              </span>
              <Button size="small" variant="outlined" className="!border-amber-400/60 !text-amber-100" onClick={() => void connectVault()}>
                Connect Microsoft
              </Button>
            </>
          )}
        </div>
      )}

      <div className="px-4 pb-8 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-200 text-sm">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-border-dark bg-card-dark p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total documents</div>
            <div className="text-2xl font-bold text-white mt-1">{stats?.totalDocuments ?? '—'}</div>
          </div>
          <div className="rounded-xl border border-border-dark bg-card-dark p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Active custodians</div>
            <div className="text-2xl font-bold text-white mt-1">{stats?.activeCustodians ?? '—'}</div>
          </div>
          <div className="rounded-xl border border-border-dark bg-card-dark p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Vault</div>
            <div className="text-2xl font-bold text-emerald-400 mt-1">{stats?.vaultConnected ? 'Connected' : 'Offline'}</div>
            {canConfigureVault && stats?.vaultConnected && vaultStatus?.vaultUserPrincipalName && (
              <div className="text-xs text-slate-400 mt-2 truncate" title={vaultStatus.vaultUserPrincipalName}>
                {vaultStatus.vaultUserPrincipalName}
              </div>
            )}
            {canConfigureVault && stats?.vaultConnected && vaultStatus?.graphAuthMode === 'AppOnly' && (
              <div className="text-[10px] text-slate-500 mt-1">App-only mode</div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border-dark bg-card-dark overflow-hidden">
          <div className="px-4 py-3 border-b border-border-dark flex flex-wrap gap-3 items-center justify-between">
            <h2 className="text-lg font-bold text-white">Archive</h2>
            <TextField
              size="small"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setPage(1);
                setSearchTerm(e.target.value);
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'rgba(15,23,42,0.6)' },
                '& .MuiOutlinedInput-input': { color: '#e2e8f0' },
                minWidth: 220,
              }}
            />
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-16">
                <CircularProgress size={40} />
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-elevation-dark text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3">Document</th>
                    <th className="px-4 py-3">Custodian</th>
                    <th className="px-4 py-3">Issued</th>
                    <th className="px-4 py-3 text-center">Sync</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-dark/60">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                        No documents yet.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.documentArchiveId} className="hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="material-symbols-outlined text-primary shrink-0 text-lg leading-none" aria-hidden>
                                description
                              </span>
                              <div className="font-semibold text-white min-w-0 break-words">{row.originalFileName}</div>
                            </div>
                            {(row.projectName || row.projectLabel) && (
                              <div className="text-xs text-slate-500 mt-0.5 pl-8">
                                Project: {row.projectName ?? row.projectLabel}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-200">
                          {row.custodianDisplayName ? row.custodianDisplayName : <span className="italic text-slate-500">Unassigned</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-400">{formatDate(row.uploadedAt)}</td>
                        <td className="px-4 py-3 text-center" title={row.syncStatus === 'LocalOnly' ? 'Metadata only' : 'In OneDrive'}>
                          <span
                            className={`material-symbols-outlined text-lg ${
                              row.syncStatus === 'LocalOnly'
                                ? 'text-amber-400/90'
                                : row.syncStatus === 'Synced'
                                  ? 'text-emerald-400'
                                  : 'text-slate-500'
                            }`}
                          >
                            {row.syncStatus === 'LocalOnly' ? 'cloud_off' : row.syncStatus === 'Synced' ? 'cloud_done' : 'sync'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="action-row-icon-buttons">
                          <button
                            type="button"
                            className="text-primary hover:bg-primary/10"
                            onClick={() => void handleView(row)}
                            aria-label="View document"
                            title="View"
                          >
                            <span className="material-symbols-outlined">visibility</span>
                          </button>
                          {canCreate && stats?.vaultConnected && (row.hasCloudCopy === false || row.syncStatus === 'LocalOnly') && (
                            <button
                              type="button"
                              className="text-amber-300/90 hover:bg-amber-500/10"
                              onClick={() => void openCloudUpload(row)}
                              aria-label="Upload file to cloud"
                              title="Upload to cloud"
                            >
                              <span className="material-symbols-outlined">cloud_upload</span>
                            </button>
                          )}
                          {canEdit && (
                            <button
                              type="button"
                              className="text-slate-300 hover:bg-white/5"
                              onClick={() => void openAssign(row)}
                              aria-label="Edit document metadata"
                              title="Edit metadata"
                            >
                              <span className="material-symbols-outlined">edit</span>
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-slate-300 hover:bg-white/5"
                            onClick={() => void openHistory(row)}
                            aria-label="View custodian history"
                            title="History"
                          >
                            <span className="material-symbols-outlined">history</span>
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              className="text-red-400 hover:bg-red-500/10"
                              onClick={() => void handleDelete(row)}
                              aria-label="Delete document"
                              title="Delete"
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {totalCount > 0 && (
            <div className="px-4 py-2 border-t border-border-dark flex items-center justify-between text-xs text-slate-500">
              <span>
                Page {page} of {totalPages} ({totalCount} total)
              </span>
              <div className="flex gap-2">
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="!text-slate-300"
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="!text-slate-300"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="md" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle sx={listPageDialogTitleSx}>Add to archive</DialogTitle>
        <DialogContent sx={listPageDialogContentSx}>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">File (optional — add later with &quot;To cloud&quot;)</div>
              <Button variant="outlined" component="label" className="!mt-0">
                Choose file
                <input
                  type="file"
                  hidden
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                />
              </Button>
              {uploadFile && <span className="ml-2 text-sm text-slate-400 align-middle">{uploadFile.name}</span>}
              {uploadFile && !stats?.vaultConnected && (
                <div className="text-xs text-amber-200/80 mt-2">
                  Connect the shared Microsoft vault to upload a file. You can still save metadata only without a file.
                </div>
              )}
            </div>
            <TextField
              label="Document name"
              value={uploadDocumentName}
              onChange={(e) => setUploadDocumentName(e.target.value)}
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              placeholder={uploadFile ? 'Optional when a file is selected' : 'e.g. Site survey — Building A'}
              helperText={
                uploadFile
                  ? 'The name stored in the archive and OneDrive matches the file you choose above (unsafe characters are adjusted). This field is optional and not used for that file name.'
                  : 'Required. Used as the name for a metadata-only entry (no file yet).'
              }
            />
            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              label="Subfolder under archive root (optional)"
              value={targetFolderSubPath}
              onChange={(e) => setTargetFolderSubPath(e.target.value)}
              fullWidth
              placeholder={stats ? `${stats.defaultArchiveRootFolder}/` : 'e.g. Q1/Eng'}
              helperText="Relative to the default archive root. Use Browse to pick a folder in OneDrive (vault required)."
              sx={{ '& .MuiOutlinedInput-root': { minWidth: 0 } }}
              InputProps={
                stats?.vaultConnected
                  ? {
                      endAdornment: (
                        <InputAdornment position="end" sx={{ ml: 0, maxHeight: 32, alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openFolderDialog('add')}
                            sx={{ flexShrink: 0, minWidth: 'auto', px: 1.5, py: 0.5, lineHeight: 1.2 }}
                          >
                            Browse
                          </Button>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="upload-project-label">Project (optional)</InputLabel>
              <Select
                labelId="upload-project-label"
                label="Project (optional)"
                value={uploadProjectId}
                onChange={(e) => setUploadProjectId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {projectOptions.map((project) => (
                  <MenuItem key={project.projectId} value={project.projectId}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="upload-custodian-label">Custodian (optional)</InputLabel>
              <Select
                labelId="upload-custodian-label"
                label="Custodian (optional)"
                value={uploadCustodian}
                onChange={(e) => setUploadCustodian(e.target.value as number | '')}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {custodianOptions.map((o) => (
                  <MenuItem key={o.userId} value={o.userId}>
                    {o.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {canCreate && shareableUsers.length > 0 && (
              <Autocomplete<ShareableUser, true, false, false>
                multiple
                options={shareableUsers}
                getOptionLabel={(o) => `${o.displayName} (${o.email})`}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                value={uploadShareWith}
                onChange={(_e, v) => setUploadShareWith(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="Share in Microsoft 365 (optional)"
                    placeholder="Select users for a people-only link"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, i) => (
                    <Chip {...getTagProps({ index: i })} key={opt.userId} size="small" label={opt.displayName} />
                  ))
                }
              />
            )}
          </div>
        </DialogContent>
        <DialogActions sx={listPageDialogActionsSx}>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void submitUpload()} disabled={uploading}>
            {uploading ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignOpen} onClose={() => !assigning && setAssignOpen(false)} maxWidth="sm" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle sx={listPageDialogTitleSx}>Edit document metadata</DialogTitle>
        <DialogContent sx={listPageDialogContentSx}>
          <div className="space-y-4">
            <div className="text-xs text-slate-500">
              Update metadata only. File upload/replacement and folder changes are intentionally disabled here.
            </div>
            <TextField
              label="Document name"
              value={assignDocumentName}
              onChange={(e) => setAssignDocumentName(e.target.value)}
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
            />
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="assign-project-label">Project (optional)</InputLabel>
              <Select
                labelId="assign-project-label"
                label="Project (optional)"
                value={assignProjectId}
                onChange={(e) => setAssignProjectId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {projectOptions.map((project) => (
                  <MenuItem key={project.projectId} value={project.projectId}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="assign-custodian-label">Custodian</InputLabel>
              <Select
                labelId="assign-custodian-label"
                label="Custodian"
                value={assignValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'unassigned') setAssignValue('unassigned');
                  else setAssignValue(Number(v));
                }}
              >
                <MenuItem value="unassigned">
                  <em>Unassigned</em>
                </MenuItem>
                {custodianOptions.map((o) => (
                  <MenuItem key={o.userId} value={o.userId}>
                    {o.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {shareableUsers.length > 0 && (
              <Autocomplete<ShareableUser, true, false, false>
                multiple
                options={shareableUsers}
                getOptionLabel={(o) => `${o.displayName} (${o.email})`}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                value={assignShareWith}
                onChange={(_e, v) => setAssignShareWith(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="Share in Microsoft 365 (optional)"
                    placeholder="Select users for a people-only link"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, i) => (
                    <Chip {...getTagProps({ index: i })} key={opt.userId} size="small" label={opt.displayName} />
                  ))
                }
              />
            )}
          </div>
        </DialogContent>
        <DialogActions sx={listPageDialogActionsSx}>
          <Button onClick={() => setAssignOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void submitAssign()} disabled={assigning}>
            {assigning ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle sx={listPageDialogTitleSx}>Custodian history — {historyTitle}</DialogTitle>
        <DialogContent sx={listPageDialogContentSx}>
          {historyRows.length === 0 ? (
            <p className="text-slate-500 text-sm py-1">No custodian changes recorded.</p>
          ) : (
            <ul className="space-y-3">
              {historyRows.map((log) => (
                <li key={log.logId} className="border-l-2 border-primary pl-3 py-1">
                  <div className="text-sm text-white font-medium">{logSummary(log)}</div>
                  <div className="text-xs text-slate-500">
                    {formatDate(log.changedAt)} · by {log.changedByDisplayName}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
        <DialogActions sx={listPageDialogActionsSx}>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folderDialogOpen} onClose={() => !folderLoading && setFolderDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle sx={listPageDialogTitleSx}>Pick folder in OneDrive</DialogTitle>
        <DialogContent sx={listPageDialogContentSx}>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Current path</div>
              <p className="text-sm text-slate-300 break-all font-mono">{folderBrowsePath}</p>
            </div>
            <div className="flex gap-2 items-center">
              <Button
                size="small"
                variant="outlined"
                disabled={(() => {
                  const root = (stats?.defaultArchiveRootFolder ?? 'DocumentArchives').replace(/\\/g, '/');
                  const f = folderBrowsePath.replace(/\\/g, '/');
                  return f === root;
                })()}
                onClick={() => goToParentFolder()}
              >
                Up
              </Button>
              {folderLoading && <CircularProgress size={22} />}
            </div>
            <ul className="max-h-56 overflow-y-auto rounded border border-border-dark/50 divide-y divide-border-dark/40 text-sm">
              {folderEntries
                .filter((e) => e.isFolder)
                .map((e) => (
                  <li key={e.id + e.name}>
                    <button
                      type="button"
                      className="w-full text-left px-2 py-2 hover:bg-white/5 text-slate-200 flex items-center gap-2"
                      onClick={() => enterSubfolder(e.name)}
                    >
                      <span className="material-symbols-outlined text-lg">folder</span> {e.name}
                    </button>
                  </li>
                ))}
              {folderEntries.filter((e) => e.isFolder).length === 0 && !folderLoading && (
                <li className="px-2 py-3 text-slate-500">No subfolders. Create one below or use this folder.</li>
              )}
            </ul>
            <div className="flex flex-wrap gap-2 items-end">
              <TextField
                size="small"
                variant="outlined"
                margin="dense"
                label="New folder"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void createSubfolder();
                  }
                }}
              />
              <Button size="small" variant="outlined" onClick={() => void createSubfolder()} disabled={folderLoading}>
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={listPageDialogActionsSx}>
          <Button onClick={() => setFolderDialogOpen(false)} disabled={folderLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => applyPickedFolder()} disabled={folderLoading}>
            Use this folder
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloudOpen} onClose={() => !clouding && setCloudOpen(false)} maxWidth="md" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle sx={listPageDialogTitleSx}>
          Upload to cloud{cloudDoc ? ` — ${cloudDoc.originalFileName}` : ''}
        </DialogTitle>
        <DialogContent sx={listPageDialogContentSx}>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Choose a subfolder under the shared archive (same as Add to archive) and a file. The name in the list may be
              adjusted to include the file extension.
            </p>
            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              label="Subfolder under archive root (optional)"
              value={cloudTargetFolderSubPath}
              onChange={(e) => setCloudTargetFolderSubPath(e.target.value)}
              fullWidth
              disabled={!stats?.vaultConnected}
              placeholder={stats ? `${stats.defaultArchiveRootFolder}/` : 'e.g. Q1/Eng'}
              helperText="Relative to the default archive root. Use Browse to pick a folder in OneDrive (vault required)."
              sx={{ '& .MuiOutlinedInput-root': { minWidth: 0 } }}
              InputProps={
                stats?.vaultConnected
                  ? {
                      endAdornment: (
                        <InputAdornment position="end" sx={{ ml: 0, maxHeight: 32, alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openFolderDialog('cloud')}
                            sx={{ flexShrink: 0, minWidth: 'auto', px: 1.5, py: 0.5, lineHeight: 1.2 }}
                          >
                            Browse
                          </Button>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
            <Button variant="outlined" component="label" fullWidth>
              Choose file
              <input type="file" hidden onChange={(e) => setCloudFile(e.target.files?.[0] ?? null)} />
            </Button>
            {cloudFile && <p className="text-sm text-slate-500 break-all">{cloudFile.name}</p>}
          </div>
        </DialogContent>
        <DialogActions sx={listPageDialogActionsSx}>
          <Button onClick={() => setCloudOpen(false)} disabled={clouding}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void submitCloudUpload()} disabled={clouding || !cloudFile}>
            {clouding ? <CircularProgress size={22} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {deleteConfirmationDialog}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} className="!bg-elevation-dark !text-slate-100">
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </DarkLayout>
  );
};

export default DocumentArchives;
